
import { Component, Mixins, Prop } from 'vue-property-decorator'
import Testable from '@/mixins/Testable'
import { Transport } from '@/models/Transport'
import { TerminalVisit } from '@/models/TerminalVisit'
import { BillOfLading } from '@/models/BillOfLading'
import { namespace } from 'vuex-class'
import VesselDocuments from '@/views/Preferences/VesselDocuments.vue'
import User from '@/models/User'
import LoadingBillNewDialog from '../views/LoadingBill/NewDialog.vue'
import InvoiceCreateDialog from '../views/Invoicing/CreateDialog.vue'
import InvoiceEditDialog from '../views/Invoicing/EditDialog.vue'
import InvoiceGeneratorDialogWrapper from '../views/Invoicing/GeneratorDialog.vue'
import InvoiceEmailDialog from '../views/Invoicing/EmailDialog.vue'
import ExternalDownloadDialog from '../views/Invoicing/ExternalDownloadDialog.vue'
import InvoiceType from '@/enums/InvoiceType'
import { Invoice } from '@/models/accounting/Invoice'
import { generateInvoicePdf, GenerateInvoiceData } from '@/utils/invoice'
import Notification from '@/classes/Notification'
import i18n from '@/i18n'
import { confirmOutage } from '@/classes/Availability'
import { isV3DeepLink, v3PagePath } from '@/utils/v3Page'
import { FlatTransport } from '@/models/FlatTransport'
import { fetchFlatTransportsById } from '@/utils/flatTransports'
import TransportationType from '@/enums/TransportationType'
import DownloadDocumentDialog from '@/components/Transport/Accounting/DownloadDocumentDialog.vue'
import PreparePurchaseOrderDialog from '@/components/Transport/Accounting/PreparePurchaseOrderDialog.vue'
import TransportInvoiceCreatorDialog from '@/components/Transport/Accounting/InvoiceCreatorDialog.vue'
import CloseAllTransportsDialog from '@/components/Transport/CloseAllTransportsDialog.vue'
import TrackAndTraceShare from '@/components/TrackAndTraceShare.vue'
import { filteroutRedirected } from '@/utils/routing'

const Viewport = namespace('viewport')

// The V2 dialogs the V3 transports page opens for its selection (BGT-7489)
type TransportBulkDialog = 'documents' | 'purchaseOrder' | 'invoice' | 'close'

@Component({
	components: {
		VesselDocuments,
		LoadingBillNewDialog,
		InvoiceCreateDialog,
		InvoiceEditDialog,
		InvoiceGeneratorDialogWrapper,
		InvoiceEmailDialog,
		ExternalDownloadDialog,
		DownloadDocumentDialog,
		PreparePurchaseOrderDialog,
		TransportInvoiceCreatorDialog,
		CloseAllTransportsDialog,
		TrackAndTraceShare
	}
})
export default class V3Page extends Mixins(Testable) {
	@Viewport.Getter('isMobile')
	isMobile

	@Prop({ required: true })
	page!: string

	@Prop({ default: null })
	routeParams

	@Prop({ default: false })
	isDialog!: boolean

	msgEventListener!: (evt: MessageEvent) => void

	lastVisitedUrl: string = ''
	transportAndVisitLoading = false
	openBillOfLadingGenerator: null | {
		transport: Transport | null
		terminalVisit: TerminalVisit
		onSubmit: (bill: BillOfLading) => void
	} = null
	vesselId: null | string = null
	iframeSrc: string = ''
	isVesselDocumentsModalOpen: boolean | null = null
	isLoadingBillFormModalOpen: boolean | null = null

	billOfLadingType: 'loading-bill' | 'unloading-bill' = 'unloading-bill'

	// Invoice dialog state
	isInvoiceCreateDialogOpen: boolean | null = null
	invoiceCreateType: InvoiceType = InvoiceType.SALES
	invoiceCreateCallback: ((response?: any) => void) | null = null

	isInvoiceEditDialogOpen: boolean | null = null
	invoiceEditId: string | null = null
	invoiceEditCallback: ((response?: any) => void) | null = null

	isInvoiceGeneratorDialogOpen: boolean | null = null
	invoiceGeneratorId: string | null = null
	invoiceGeneratorCallback: ((response?: any) => void) | null = null

	isInvoiceEmailDialogOpen: boolean | null = null
	invoiceEmailId: string | null = null
	invoiceEmailCallback: ((response?: any) => void) | null = null

	isExternalDownloadDialogOpen: boolean | null = null
	externalDownloadInvoiceId: string | null = null
	externalDownloadCallback: ((response?: any) => void) | null = null

	// Transport dialogs for the V3 transports page
	trackTrace: { transportId: string; cargoId: string } | null = null
	trackTraceCallback: ((response?: any) => void) | null = null

	transportsLoading = false
	transportBulkDialog: TransportBulkDialog | null = null
	bulkTransports: Array<FlatTransport> = []
	bulkTransportationType: TransportationType = TransportationType.VESSEL
	bulkInvoiceType: InvoiceType = InvoiceType.SALES
	bulkInvoicePreview = false
	bulkResult: any = null
	transportBulkCallback: ((response?: any) => void) | null = null

	created() {
		this.iframeSrc = this.baseUrl + this.withRouteParams(this.page)
	}

	withRouteParams(page: string): string {
		const url = new URL('http://example.com' + page)

		if (this.isDialog) {
			url.searchParams.append('dialog', 'true')
		}

		if (!this.routeParams) return url.toString().replace('http://example.com', '')

		Object.entries(this.routeParams).forEach(([key, value]) => {
			if (value !== null && value !== undefined) {
				url.searchParams.append(key, String(value))
			}
		})

		return url.toString().replace('http://example.com', '')
	}

	get localStorageKey() {
		return v3PagePath(this.page) + '_last_url'
	}

	get baseUrl(): string {
		return String(process.env.VUE_APP_TERMINAL_MODULE_URL).replace(/.*(\/)$/gm, '')
	}

	get spaURL(): string {
		if (this.lastVisitedUrl && this.lastVisitedUrl.startsWith(v3PagePath(this.page))) return this.lastVisitedUrl
		return this.page
	}

	get iframeElement(): HTMLIFrameElement | null {
		return (this.$refs.iframe as HTMLIFrameElement) ?? null
	}

	handleRouteChanged(url: string) {
		if (url.startsWith(v3PagePath(this.page))) window.localStorage.setItem(this.localStorageKey, url)
	}

	beforeCreate(): void {
		this.msgEventListener = (event: MessageEvent): void => {
			const data = String(event.data)

			if (!data.startsWith('xframe:')) {
				return
			}

			if (data.startsWith('xframe:fire:')) {
				const eventAndArgs = data.replace('xframe:fire:', '').split(':')
				const event = eventAndArgs[0]
				const args = eventAndArgs.slice(1).join(':')

				return this.handleEvent(event, args ? JSON.parse(args) : null)
			}

			if (data.startsWith('xframe:call:request:')) {
				const eventUuidAndArgs = data.replace('xframe:call:request:', '').split(':')
				const event = eventUuidAndArgs[0]
				const uuid = eventUuidAndArgs[1]

				const args = eventUuidAndArgs.slice(2).join(':')

				this.handleCall(event, args ? JSON.parse(args) : null, (response) => {
					const terminalIframeOrigin = this.spaURL.startsWith('http')
						? new URL(this.spaURL).origin
						: window.location.origin

					this.iframeElement?.contentWindow?.postMessage(
						`xframe:call:response:${event}:${uuid}:${JSON.stringify(response || null)}`,
						terminalIframeOrigin
					)
				})

				return
			}

			throw new Error(`Could not handle ${event.data}`)
		}

		window.addEventListener('message', this.msgEventListener)
	}

	handleEvent(event: string, args: any = {}): void {
		switch (event) {
			case 'navigate':
				// replace: a hand-over to a V2 page (createEmptyReportForBid) must not stay behind in the
				// history, or Back lands on the V3 page that hands over again. The target may redirect to
				// add its query defaults (transports-old adds transportationType).
				if (args?.replace) {
					const { replace, ...location } = args
					this.$router.replace(location).catch(filteroutRedirected)
				} else {
					this.$router.push(args).catch(filteroutRedirected)
				}
				break
			case 'routeChanged':
				this.handleRouteChanged(args)
				break
			case 'syncUser':
				User.sync(true)
				break
			case 'profileUpdated':
				this.handleProfileUpdated(args)
				break
			case 'closeDialog':
				this.$emit('closeDialog', args)
				break
			case 'apiUnreachable':
				// the embedded app saw a run of gateway errors; V2 checks for itself before switching (BGT-8224)
				confirmOutage()
				break
			default:
				throw new Error(`Unknown event ${event}`)
		}
	}

	async handleCall(event: string, args: any, cb: (response?: any) => void): Promise<any> {
		switch (event) {
			case 'show-loading-bill-modal':
				await this.handleLoadingBillModal(args, cb)
				break
			case 'show-vessel-documents-modal':
				await this.handleVesselDocumentsModal(args, cb)
				break
			case 'show-loading-bill-form-modal':
				await this.handleLoadingBillFormModal(cb)
				break
			case 'show-invoice-creator-modal':
				await this.handleInvoiceCreatorModal(args, cb)
				break
			case 'show-invoice-editor-modal':
				await this.handleInvoiceEditorModal(args, cb)
				break
			case 'show-invoice-generator-modal':
				await this.handleInvoiceGeneratorModal(args, cb)
				break
			case 'show-invoice-email-modal':
				await this.handleInvoiceEmailModal(args, cb)
				break
			case 'show-external-invoice-download-modal':
				await this.handleExternalInvoiceDownloadModal(args, cb)
				break
			case 'show-track-trace-modal':
				this.handleTrackTraceModal(args, cb)
				break
			case 'show-transport-documents-download-modal':
				await this.openTransportBulkDialog('documents', args, cb)
				break
			case 'show-prepare-purchase-order-modal':
				await this.openTransportBulkDialog('purchaseOrder', args, cb)
				break
			case 'show-transport-invoice-creator-modal':
				this.bulkInvoiceType = args?.invoiceType ?? InvoiceType.SALES
				await this.openTransportBulkDialog('invoice', args, cb)
				break
			case 'show-close-transports-modal':
				await this.openTransportBulkDialog('close', args, cb)
				break
			default:
				throw new Error(`Unknown event ${event}`)
		}
	}

	handleInvoiceCreatorModal(
		args: { invoiceType?: InvoiceType },
		callback: (response?: any) => void
	): void {
		this.invoiceCreateType = args?.invoiceType ?? InvoiceType.SALES
		this.isInvoiceCreateDialogOpen = true
		this.invoiceCreateCallback = callback
	}

	handleInvoiceEditorModal(
		args: { invoiceId: string },
		callback: (response?: any) => void
	): void {
		this.invoiceEditId = args.invoiceId
		this.isInvoiceEditDialogOpen = true
		this.invoiceEditCallback = callback
	}

	handleInvoiceGeneratorModal(
		args: { invoiceId: string },
		callback: (response?: any) => void
	): void {
		this.invoiceGeneratorId = args.invoiceId
		this.isInvoiceGeneratorDialogOpen = true
		this.invoiceGeneratorCallback = callback
	}

	handleInvoiceEmailModal(
		args: { invoiceId: string },
		callback: (response?: any) => void
	): void {
		this.invoiceEmailId = args.invoiceId
		this.isInvoiceEmailDialogOpen = true
		this.invoiceEmailCallback = callback
	}

	handleExternalInvoiceDownloadModal(
		args: { invoiceId: string },
		callback: (response?: any) => void
	): void {
		this.externalDownloadInvoiceId = args.invoiceId
		this.isExternalDownloadDialogOpen = true
		this.externalDownloadCallback = callback
	}

	handleTrackTraceModal(args: { transportId: string; cargoId: string }, callback: (response?: any) => void): void {
		this.trackTrace = { transportId: String(args.transportId), cargoId: String(args.cargoId) }
		this.trackTraceCallback = callback
	}

	onTrackTraceClose(): void {
		this.trackTrace = null
		this.trackTraceCallback?.(null)
		this.trackTraceCallback = null
	}

	// The V3 page only sends ids; the V2 dialogs work on FlatTransports, so load those first.
	async openTransportBulkDialog(
		dialog: TransportBulkDialog,
		args: { transportIds: Array<string>; transportationType?: TransportationType },
		callback: (response?: any) => void
	): Promise<void> {
		this.transportsLoading = true
		try {
			this.bulkTransports = await fetchFlatTransportsById((args?.transportIds ?? []).map(String))
		} catch (err) {
			// eslint-disable-next-line
			console.error('Error loading the transports', err)
			await Notification.Error(i18n.t('errors.generic').toString())
			callback(null)
			return
		} finally {
			this.transportsLoading = false
		}

		this.bulkTransportationType = args?.transportationType ?? TransportationType.VESSEL
		this.bulkInvoicePreview = false
		this.bulkResult = null
		this.transportBulkCallback = callback
		this.transportBulkDialog = dialog
	}

	async onTransportInvoiceCreated(): Promise<void> {
		this.bulkResult = { created: true }
		await Notification.Success(i18n.t('transports.combinedInvoiceSuccess').toString())
	}

	onTransportsClosed(transports: Array<FlatTransport>): void {
		this.bulkResult = { closedTransportIds: transports.map((transport) => transport.getApiId()) }
		this.onTransportBulkDialogClose()
	}

	// Answers on the next tick: InvoiceCreator emits closed before invoiceCreated.
	async onTransportBulkDialogClose(): Promise<void> {
		const callback = this.transportBulkCallback
		this.transportBulkCallback = null
		this.transportBulkDialog = null

		await this.$nextTick()

		callback?.(this.bulkResult)
		this.bulkResult = null
		this.bulkTransports = []
	}

	// Invoice dialog close/success handlers

	onInvoiceCreateClose(): void {
		this.isInvoiceCreateDialogOpen = null
		this.invoiceCreateCallback?.(null)
		this.invoiceCreateCallback = null
	}

	onInvoiceCreateSaved(event?: any): void {
		this.fireToSpa('invoiceCreated', event ?? {})
		this.invoiceCreateCallback?.(event)
		this.invoiceCreateCallback = null
		this.isInvoiceCreateDialogOpen = null
	}

	onInvoiceEditClose(): void {
		this.isInvoiceEditDialogOpen = null
		this.invoiceEditCallback?.(null)
		this.invoiceEditCallback = null
	}

	onInvoiceEditSaved(event?: any): void {
		this.fireToSpa('invoiceUpdated', event)
		this.invoiceEditCallback?.(event)
		this.invoiceEditCallback = null
		this.isInvoiceEditDialogOpen = null
	}

	onInvoiceGeneratorClose(): void {
		this.isInvoiceGeneratorDialogOpen = null
		this.invoiceGeneratorCallback?.(null)
		this.invoiceGeneratorCallback = null
	}

	// Closes the dialog first and only then talks to the backend, so the user isn't kept waiting
	// on generate + the attachments patch. This lives here rather than in the dialog because the
	// dialog is v-if'd away on close: it would unmount mid-request and its $emit would go nowhere.
	async onInvoiceGenerateRequest(payload: {
		invoice: Invoice
		receivedData: GenerateInvoiceData | null
	}): Promise<void> {
		const invoiceId = this.invoiceGeneratorId
		const resolveSpaCall = this.invoiceGeneratorCallback
		let result: { documentId: string } | null = null

		this.isInvoiceGeneratorDialogOpen = null
		this.invoiceGeneratorCallback = null

		// The SPA cannot tell "generating in the background" from "dialog cancelled" on its own:
		// both leave its call pending. This is what switches its row indicator on; the
		// invoiceUpdated below switches it off again.
		this.fireToSpa('invoiceGenerating', { invoiceId })

		try {
			const { documentId } = await generateInvoicePdf(payload.invoice, payload.receivedData)
			result = { documentId }
		} catch (err) {
			// eslint-disable-next-line
			console.error('Error generating invoice PDF', err)
			await Notification.Error(i18n.t('errors.generation_error').toString())
		} finally {
			// Refresh either way: the PDF may exist even when the attachments patch failed.
			this.fireToSpa('invoiceUpdated', { invoiceId })
			// Resolving late is what lets the SPA start the download once the PDF actually exists.
			resolveSpaCall?.(result)
		}
	}

	onInvoiceEmailClose(): void {
		this.isInvoiceEmailDialogOpen = null
		this.invoiceEmailCallback?.(null)
		this.invoiceEmailCallback = null
	}

	onInvoiceEmailSent(event?: any): void {
		this.fireToSpa('invoiceUpdated', event)
		this.invoiceEmailCallback?.(event)
		this.invoiceEmailCallback = null
		this.isInvoiceEmailDialogOpen = null
	}

	onExternalDownloadClose(): void {
		this.isExternalDownloadDialogOpen = null
		this.externalDownloadCallback?.(null)
		this.externalDownloadCallback = null
	}

	beforeDestroy(): void {
		window.removeEventListener('message', this.msgEventListener)
	}

	async navigateTo(page: string) {
		await this.$nextTick()
		// A page with a query is a deep link (a push notification, a mail button) and must be shown
		// as is; only a bare page falls back to where the user last was under it.
		this.lastVisitedUrl = isV3DeepLink(page)
			? page
			: (window.localStorage.getItem(this.localStorageKey) ?? page)
		this.iframeElement?.contentWindow?.postMessage(`navigate:${this.lastVisitedUrl}`, '*')
	}

	async handleLoadingBillModal(
		args: { transportId: string; terminalVisitId: string },
		callback: (response?: any) => void
	): Promise<void> {
		this.transportAndVisitLoading = true
		let transportQuery = Promise.resolve(null as any)
		let terminalVisitQuery = Promise.resolve(null as any)
		if (args.transportId) {
			transportQuery = Transport.find(args.transportId)
		}
		if (args.terminalVisitId) {
			terminalVisitQuery = TerminalVisit.with([
				'product',
				'vessel',
				'terminal',
				'terminal.company',
				'terminal.ownedBy',
				'extractionArea',
				'vessel.company'
			]).find(args.terminalVisitId)
		}

		const transport = (await transportQuery)?.getData() ?? null
		const terminalVisit = (await terminalVisitQuery)?.getData() ?? null

		this.billOfLadingType =
			terminalVisit?.transshipmentDirection === 'unloading' ? 'unloading-bill' : 'loading-bill'
		this.transportAndVisitLoading = false

		this.openBillOfLadingGenerator = {
			transport,
			terminalVisit,
			onSubmit: () => {
				callback()

				this.openBillOfLadingGenerator = null
			}
		}
	}

	async handleVesselDocumentsModal(args: { vesselId: string }, callback: (response?: any) => void): Promise<void> {
		this.vesselId = args.vesselId
		this.isVesselDocumentsModalOpen = true
		callback()
	}

	async handleLoadingBillFormModal(callback: (response?: any) => void): Promise<void> {
		this.isLoadingBillFormModalOpen = true
		callback()
	}

	async handleProfileUpdated(args: { done: boolean } = { done: false }): Promise<void> {
		await User.sync(true)
		if (args.done) {
			await this.$router.push('/')
		}
	}

	fireToSpa(event: string, payload?: any): void {
		const spaUrl = process.env.VUE_APP_TERMINAL_MODULE_URL || window.location.origin
		const targetOrigin = spaUrl.startsWith('http') ? new URL(spaUrl).origin : window.location.origin

		this.iframeElement?.contentWindow?.postMessage(
			`xframe:fire:${event}:${JSON.stringify(payload ?? {})}`,
			targetOrigin
		)
	}
}
